
import React, { useState, useEffect, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { MdAdd, MdVisibility, MdInput, MdMoreVert, MdDownload, MdEdit, MdClose, MdSearch } from 'react-icons/md';
import { FaWhatsapp } from 'react-icons/fa';
import Swal from 'sweetalert2';
import axios from 'axios';
import { GET_PENDING_PO_API, GET_COMPLETED_PO_API, NEW_FORCE_COMPLETE_PO_API, SEND_WHATSAPP_PDF_API, GET_WHATSAPP_CREDS_API, GET_PO_LIST_API, GET_CANCELLED_PO_API, GET_PO_DETAILS_BY_PO_ID_API, UPDATE_PO_API, CANCEL_PO_API, GENERATE_PO_PDF_API, TOGGLE_PO_NOTIFICATION_API, BASE_API } from '../config/API';
import Loader from '../components/common/Loader';
import NoData from '../components/common/NoData';
import Pagination from '../components/common/Pagination';
import ActionMenuPortal from '../components/common/ActionMenuPortal';
import { toast } from 'react-toastify';
import { useUser } from '../context/UserContext';
import './POManagement.css'; // Custom styles for this page
import { formatDate } from '../utils/dateUtils';

const POManagement = () => {
    const navigate = useNavigate();
    const [orders, setOrders] = useState([]);
    const [loading, setLoading] = useState(true);
    const [currentPage, setCurrentPage] = useState(1);
    const [totalPages, setTotalPages] = useState(1);
    const [totalRecords, setTotalRecords] = useState(0);
    const [activeTab, setActiveTab] = useState('pending'); // 'pending' or 'completed'
    const [searchTerm, setSearchTerm] = useState('');
    const [debouncedSearch, setDebouncedSearch] = useState('');
    const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);

    useEffect(() => {
        const handleResize = () => setIsMobile(window.innerWidth <= 768);
        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, []);

    // Menu State
    const [openMenuId, setOpenMenuId] = useState(null);
    const [anchorRect, setAnchorRect] = useState(null);

    // WhatsApp Share State
    const [showWhatsappModal, setShowWhatsappModal] = useState(false);
    const [selectedVendorsForWhatsapp, setSelectedVendorsForWhatsapp] = useState([]);
    const [sendingWhatsapp, setSendingWhatsapp] = useState(false);
    const [whatsappCreds, setWhatsappCreds] = useState(null);
    const [selectedPoForWhatsapp, setSelectedPoForWhatsapp] = useState(null);
    const [loadingVendors, setLoadingVendors] = useState(false);
    const [whatsappMessage, setWhatsappMessage] = useState("");

    // PO Action Modal State
    const [showActionModal, setShowActionModal] = useState(false);
    const [actionModalData, setActionModalData] = useState({ po: null, newStatus: '', actionTitle: '' });
    const [actionRemark, setActionRemark] = useState('');
    const [submittingAction, setSubmittingAction] = useState(false);

    const { hasPermission } = useUser();

    const toggleMenu = (id, e) => {
        e.stopPropagation();
        if (openMenuId === id) {
            setOpenMenuId(null);
            setAnchorRect(null);
        } else {
            setOpenMenuId(id);
            setAnchorRect(e.currentTarget.getBoundingClientRect());
        }
    };

    // Close menu when clicking outside
    useEffect(() => {
        const handleClickOutside = (event) => {
            if (event.target.closest('.swal2-container')) return;
            if (event.target.closest('.action-portal-wrapper')) return;
            if (event.target.closest('.action-menu-btn')) return;

            setOpenMenuId(null);
            setAnchorRect(null);
        };

        document.addEventListener('click', handleClickOutside, true);
        return () => {
            document.removeEventListener('click', handleClickOutside, true);
        };
    }, []);

    const openWhatsappModal = async (po) => {
        setOpenMenuId(null);
        setSelectedPoForWhatsapp(po);
        setShowWhatsappModal(true);
        setLoadingVendors(true);

        try {
            const token = localStorage.getItem('accessToken');

            // Fetch full PO details to ensure we have all vendor info (especially phone numbers)
            // Note: Since we are using dummy data, we might need to mock this part or handle failures gracefully if ID doesn't exist
            let requestVendors = [];

            try {
                const poResponse = await axios.get(GET_PO_DETAILS_BY_PO_ID_API(po.id), {
                    headers: { Authorization: `Bearer ${token}` }
                });
                if (poResponse.data.status && poResponse.data.data) {
                    const data = poResponse.data.data;
                    requestVendors = data.PurchaseOrderVendors ||
                        data.RequestVendors ||
                        (data.RequestVendor ? [data.RequestVendor] : []);
                } else {
                    requestVendors = po.PurchaseOrderVendors ||
                        (po.RequestVendor ? [po.RequestVendor] : []);
                }
            } catch (err) {
                console.warn("Failed to fetch real PO details. Using local data.", err);
                requestVendors = po.PurchaseOrderVendors || [];
            }

            const uniqueVendors = requestVendors.length
                ? [...new Map(requestVendors.map(v => [v.Vendor?.id || v.Vendor?._id, v])).values()]
                : [];

            const formattedVendors = uniqueVendors.map(v => {
                const vendorData = v.Vendor || {};
                // Check multiple possible key variations for the phone number
                const mobileNumber = vendorData.whatsappNumber ||
                    vendorData.whatsAppNumber ||
                    vendorData.whatsapp_number ||
                    vendorData.contactNumber ||
                    vendorData.mobileNumber || '';

                return {
                    vendorId: vendorData.id || v.vendorId,
                    vendorName: vendorData.vendorName || 'Unknown Vendor',
                    mobileNumber: mobileNumber,
                    countryCode: vendorData.countryCode || '91',
                    isChecked: true
                };
            });

            setSelectedVendorsForWhatsapp(formattedVendors);

            // Fetch WhatsApp Creds
            // const userStr = localStorage.getItem('user');
            // if (userStr) {
            //     const user = JSON.parse(userStr);
            //     const response = await axios.get(GET_WHATSAPP_CREDS_API, {
            //         headers: { Authorization: `Bearer ${token}` }
            //     });
            //     if (response.data.status && response.data.data) {
            //         setWhatsappCreds({
            //             authKey: response.data.data.authKey || '',
            //             appKey: response.data.data.appKey || '',
            //             instance_id: response.data.data.instance_id,
            //             access_token: response.data.data.access_token
            //         });
            //     }
            // }

            // Set default WhatsApp message
            const firstVendorName = formattedVendors.length > 0 ? formattedVendors[0].vendorName : 'Vendor';
            setWhatsappMessage(`Hello ${firstVendorName},\n\nPlease find the attached Purchase Order #${po.poNumber || "N/A"}.\n\nRegards,\nTeam.`);

        } catch (error) {
            console.error('Error fetching details for WhatsApp modal:', error);
            // Don't show error for dummy data flow to keep UI usable for test
            if (!po.id || po.id < 1000) { // Assuming dummy IDs are small numbers
                console.log("Using dummy data flow");
            } else {
                Swal.fire('Error', 'Failed to load vendor details.', 'error');
            }
        } finally {
            setLoadingVendors(false);
        }
    };

    const handleWhatsappCheckboxChange = (vendorId) => {
        setSelectedVendorsForWhatsapp(prev => prev.map(v =>
            v.vendorId === vendorId ? { ...v, isChecked: !v.isChecked } : v
        ));
    };

    const handleSendWhatsapp = async () => {
        const vendorsToSend = selectedVendorsForWhatsapp.filter(v => v.isChecked);
        if (vendorsToSend.length === 0) {
            Swal.fire('Error', 'Please select at least one vendor.', 'error');
            return;
        }

        const token = localStorage.getItem('accessToken');
        let currentCreds = whatsappCreds;

        // Fallback fetch for credentials
        // if (!currentCreds || !currentCreds.authKey || !currentCreds.appKey) {
        //     try {
        //         const userStr = localStorage.getItem('user');
        //         if (userStr) {
        //             const user = JSON.parse(userStr);
        //             const response = await axios.get(GET_WHATSAPP_CREDS_API, {
        //                 headers: { Authorization: `Bearer ${token}` }
        //             });
        //             if (response.data.status && response.data.data) {
        //                 currentCreds = {
        //                     authKey: response.data.data.authKey || '',
        //                     appKey: response.data.data.appKey || ''
        //                 };
        //                 setWhatsappCreds(currentCreds);
        //             }
        //         }
        //     } catch (error) {
        //         console.error("Error fetching fallback credentials:", error);
        //     }
        // }

        // if (!currentCreds || !currentCreds.authKey || !currentCreds.appKey) {
        //     Swal.fire('Error', 'WhatsApp credentials not found. Please configure them in settings.', 'error');
        //     return;
        // }

        setSendingWhatsapp(true);
        let successCount = 0;
        let failCount = 0;

        for (const vendor of vendorsToSend) {
            if (!vendor.mobileNumber) {
                failCount++;
                continue;
            }

            try {
                const payload = {
                    message: whatsappMessage,
                    file: `${BASE_API}/uploads/po/PO-${selectedPoForWhatsapp.id}.pdf`
                };

                const response = await axios.post(
                    SEND_WHATSAPP_PDF_API(selectedPoForWhatsapp.id),
                    payload,
                    { headers: { Authorization: `Bearer ${token}` } }
                );

                if (response.data.status === 'success' || response.data.status === true) {
                    successCount++;
                } else {
                    failCount++;
                }
            } catch (error) {
                console.error(`Failed to send WhatsApp to ${vendor.vendorName}:`, error);
                failCount++;
            }
        }

        setSendingWhatsapp(false);
        setShowWhatsappModal(false);

        if (successCount > 0 && failCount === 0) {
            Swal.fire('Success', `WhatsApp message sent to ${successCount} vendor(s).`, 'success');
        } else if (successCount > 0 && failCount > 0) {
            Swal.fire('Partial Success', `Sent to ${successCount} vendor(s), failed for ${failCount}.`, 'warning');
        } else {
            Swal.fire('Error', 'Failed to send WhatsApp messages.', 'error');
        }
    };


    const fetchOrders = async () => {
        try {
            setLoading(true);
            const token = localStorage.getItem('accessToken');
            const limit = 10;

            let url = activeTab === 'pending' ? GET_PENDING_PO_API :
                activeTab === 'completed' ? GET_COMPLETED_PO_API :
                    activeTab === 'cancelled' ? GET_CANCELLED_PO_API :
                        GET_PO_LIST_API;

            let params = {
                page: currentPage,
                limit: limit,
                search: debouncedSearch || undefined
            };

            // if (activeTab === 'cancelled') {
            //     params.status = 'cancelled';
            // } else if (activeTab === 'completed') {
            //     params.status = 'completed,force_completed';
            // }

            const response = await axios.get(url, {
                headers: { Authorization: `Bearer ${token}` },
                params: params
            });

            if (response.data.status) {
                setOrders(response.data.data || []);
                setTotalPages(response.data.totalPages || 1);
                setTotalRecords(response.data.totalRecords || 0);
            } else {
                setOrders([]);
                setTotalPages(1);
                setTotalRecords(0);
            }
        } catch (error) {
            console.error('Error fetching orders:', error);
            // Swal.fire('Error', 'Failed to fetch orders.', 'error');
        } finally {
            setLoading(false);
        }
    };

    // Debounce search input
    useEffect(() => {
        const timer = setTimeout(() => {
            setDebouncedSearch(searchTerm);
        }, 500);
        return () => clearTimeout(timer);
    }, [searchTerm]);

    // Reset to page 1 when search changes
    useEffect(() => {
        setCurrentPage(1);
    }, [debouncedSearch]);

    useEffect(() => {
        fetchOrders();
    }, [currentPage, activeTab, debouncedSearch]);

    const handlePageChange = (page) => {
        setCurrentPage(page);
    };

    const handlePoActionClick = (po, newStatus, actionTitle) => {
        setOpenMenuId(null);
        setActionModalData({ po, newStatus, actionTitle });
        setActionRemark('');
        setShowActionModal(true);
    };

    const handleGeneratePDF = async (poId) => {
        setOpenMenuId(null);
        Swal.fire({
            title: 'Generating PDF...',
            text: 'Please wait, generating your Purchase Order PDF.',
            allowOutsideClick: false,
            didOpen: () => {
                Swal.showLoading();
            }
        });

        try {
            const token = localStorage.getItem('accessToken');
            const response = await axios.get(GENERATE_PO_PDF_API(poId), {
                headers: { Authorization: `Bearer ${token}` }
            });

            if (response.data.status && response.data.pdf) {
                const fullPdfUrl = `${BASE_API}/${response.data.pdf}`;

                // To force download on all browsers, we fetch the file as a blob
                const fileResponse = await axios.get(fullPdfUrl, {
                    headers: { Authorization: `Bearer ${token}` },
                    responseType: 'blob'
                });

                const fileURL = URL.createObjectURL(new Blob([fileResponse.data]));
                const link = document.createElement('a');
                link.href = fileURL;
                link.setAttribute('download', `PO_${poId}.pdf`);
                document.body.appendChild(link);
                link.click();
                link.parentNode.removeChild(link);
                URL.revokeObjectURL(fileURL);

                Swal.close();
            } else {
                throw new Error(response.data.message || 'Failed to generate PDF URL');
            }
        } catch (error) {
            console.error('Error generating PDF:', error);
            Swal.fire('Error', 'Failed to generate PDF. Please try again.', 'error');
        }
    };

    const submitPoAction = async () => {
        const { po, newStatus } = actionModalData;

        if (newStatus === 'completed' && !actionRemark.trim()) {
            Swal.fire('Validation Error', 'You need to write a remark!', 'warning');
            return;
        }

        setSubmittingAction(true);
        try {
            const token = localStorage.getItem('accessToken');
            const payload = {
                status: newStatus
            };

            if (newStatus === 'completed') {
                payload.remarks = actionRemark;
            }

            let response;
            if (newStatus === 'cancelled') {
                response = await axios.put(CANCEL_PO_API(po.id), {}, {
                    headers: { Authorization: `Bearer ${token}` }
                });
            } else if (actionModalData.actionTitle === 'Force Complete PO') {
                // Use the new Force Complete API with remark
                response = await axios.put(NEW_FORCE_COMPLETE_PO_API(po.id), {
                    remark: actionRemark
                }, {
                    headers: { Authorization: `Bearer ${token}` }
                });
            } else {
                response = await axios.put(UPDATE_PO_API(po.id), payload, {
                    headers: { Authorization: `Bearer ${token}` }
                });
            }

            if (response.data.status) {
                Swal.fire('Success', `PO status has been updated to ${newStatus}.`, 'success');
                setShowActionModal(false);

                // Optimistic update: remove from current view as it now belongs to a different tab or filter
                setOrders(prev => prev.filter(o => o.id !== po.id));

                // If it was the last item on the page and not on page 1, go to previous page
                if (orders.length === 1 && currentPage > 1) {
                    setCurrentPage(prev => prev - 1);
                } else {
                    fetchOrders();
                }
            } else {
                Swal.fire('Error', response.data.message || `Failed to update PO status.`, 'error');
            }
        } catch (error) {
            console.error(`Error updating PO to ${newStatus}:`, error);
            Swal.fire('Error', 'An error occurred while updating the PO.', 'error');
        } finally {
            setSubmittingAction(false);
        }
    };

    const handleNotificationToggle = async (poId, currentStatus) => {
        try {
            const token = localStorage.getItem('accessToken');
            const newStatus = currentStatus === 'on' ? 'off' : 'on';

            // Optimistic UI update
            setOrders(prevOrders => prevOrders.map(order =>
                order.id === poId ? { ...order, notification: newStatus } : order
            ));

            const payload = { notification: newStatus };
            if (newStatus === 'on') {
                payload.priorityId = 2;
            }

            const response = await axios.put(TOGGLE_PO_NOTIFICATION_API(poId), payload, {
                headers: { Authorization: `Bearer ${token}` }
            });

            if (!response.data.status) {
                // Revert if failed
                setOrders(prevOrders => prevOrders.map(order =>
                    order.id === poId ? { ...order, notification: currentStatus } : order
                ));
                toast.error(response.data.message || 'Failed to update notification status');
            } else {
                toast.success(`Notification turned ${newStatus.toUpperCase()}`);
            }
        } catch (error) {
            console.error('Error toggling notification:', error);
            // Revert on error
            setOrders(prevOrders => prevOrders.map(order =>
                order.id === poId ? { ...order, notification: currentStatus } : order
            ));
            toast.error('An error occurred while updating notification status');
        }
    };





    return (
        <div className="dashboard">
            <div className="purchase-orders-header">
                <h2 className="section-title po-management-title">PO Management</h2>
            </div>

            <div className={isMobile ? "" : "card-container-po"}>
                {/* Search Bar */}
                <div className="search-container">
                    <div className="search-wrapper">
                        <MdSearch className="search-icon" />
                        <input
                            type="text"
                            placeholder="Search purchase orders..."
                            className="search-input"
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                        />
                        {searchTerm && (
                            <button
                                className="clear-search-btn"
                                onClick={() => setSearchTerm('')}
                            >
                                ✕
                            </button>
                        )}
                    </div>
                </div>
                <div className={isMobile ? "tabs-card" : "po-tabs-container"} style={{
                    borderBottom: isMobile ? 'none' : '1px solid #e5e7eb',
                    display: 'flex',
                    gap: isMobile ? '8px' : '1rem',
                    padding: isMobile ? '8px' : '1rem'
                }}>
                    <button
                        className={`tab-btn ${activeTab === 'pending' ? 'active-tab' : ''}`}
                        onClick={() => { setActiveTab('pending'); setCurrentPage(1); }}
                        style={{ flex: isMobile ? 1 : 'none' }}
                    >
                        Pending
                    </button>
                    <button
                        className={`tab-btn ${activeTab === 'completed' ? 'active-tab' : ''}`}
                        onClick={() => { setActiveTab('completed'); setCurrentPage(1); }}
                        style={{
                            padding: '0.5rem 1rem',
                            border: 'none',
                            background: activeTab === 'completed' ? '#ecfdf5' : 'transparent',
                            color: activeTab === 'completed' ? '#059669' : '#6b7280',
                            borderRadius: '6px',
                            cursor: 'pointer',
                            fontWeight: '600',
                            fontSize: '0.875rem',
                            flex: isMobile ? 1 : 'none'
                        }}
                    >
                        Completed
                    </button>
                    <button
                        className={`tab-btn ${activeTab === 'cancelled' ? 'active-tab' : ''}`}
                        onClick={() => { setActiveTab('cancelled'); setCurrentPage(1); }}
                        style={
                            {
                                padding: '0.5rem 1rem',
                                border: 'none',
                                background: activeTab === 'cancelled' ? '#fef2f2' : 'transparent',
                                color: activeTab === 'cancelled' ? '#dc2626' : '#6b7280',
                                borderRadius: '6px',
                                cursor: 'pointer',
                                fontWeight: '600',
                                fontSize: '0.875rem',
                                flex: isMobile ? 1 : 'none'
                            }
                        }
                    >
                        Cancelled
                    </button>
                </div>



                <div className="table-container">
                    <table>
                        <thead>
                            <tr>
                                <th>PO Number</th>
                                <th>Company Name</th>
                                <th>Project</th>
                                <th>Date</th>
                                <th>Total Amount</th>
                                <th>Status</th>
                                <th style={{ textAlign: 'center' }}>Notification</th>
                                <th className="table-header-actions">Actions</th>
                            </tr>
                        </thead>
                        <tbody>
                            {loading ? (
                                <tr><td colSpan="8"><Loader text="Loading orders..." /></td></tr>
                            ) : orders.length === 0 ? (
                                <tr><td colSpan="8"><NoData text={activeTab === 'pending' ? "No pending PO found" : activeTab === 'completed' ? "No complete data found" : activeTab === 'cancelled' ? "No cancelled data found" : "No orders found."} /></td></tr>
                            ) : (() => {
                                const filteredOrders = orders.filter(po => {
                                    const status = (po.status || 'pending').toLowerCase();
                                    if (activeTab === 'pending') {
                                        return status === 'pending' || status === 'partial' || status === 'partial_received' || status === 'partially received';
                                    }
                                    if (activeTab === 'completed') {
                                        return status === 'completed' || status === 'complete' || status === 'force_completed' || status === 'forced_completed' || status === 'force complete';
                                    }
                                    if (activeTab === 'cancelled') {
                                        return status === 'cancelled' || status === 'cancel';
                                    }
                                    return true;
                                });

                                if (filteredOrders.length === 0) {
                                    return <tr><td colSpan="8"><NoData text={activeTab === 'pending' ? "No pending PO found" : activeTab === 'completed' ? "No complete data found" : activeTab === 'cancelled' ? "No cancelled data found" : "No orders found."} /></td></tr>;
                                }

                                return filteredOrders.map((po) => (
                                    <tr key={po.id}>
                                        <td>
                                            <span className="po-number" style={{ fontFamily: 'monospace' }} >#{po.poNumber || "N/A"}</span>
                                        </td>
                                        <td>
                                            {
                                                // po.RequestVendor.Vendor.companyName || "N/A"
                                                po.companyName || po.RequestVendor?.Vendor?.companyName || "N/A"
                                            }
                                        </td>
                                        <td>
                                            <div className="vendor-info">
                                                <span>{po.ProjectMaster?.projectName || po.projectName}</span>
                                            </div>
                                        </td>
                                        <td>{formatDate(po.date)}</td>
                                        <td>₹{po.grandTotal ? parseFloat(po.grandTotal).toLocaleString() : '0'}</td>
                                        <td>
                                            <span className={`status-badge status-${(po.status || 'pending').toLowerCase()}`}>
                                                {po.status === 'force_completed' ? 'Force Completed' : (po.status || 'Pending')}
                                            </span>
                                        </td>
                                        <td style={{ textAlign: 'center' }}>
                                            <label className="switch">
                                                <input
                                                    type="checkbox"
                                                    checked={po.notification === 'on'}
                                                    onChange={() => handleNotificationToggle(po.id, po.notification)}
                                                />
                                                <span className="slider"></span>
                                            </label>
                                        </td>

                                        <td className={`action-cell table-cell-actions ${openMenuId === po.id ? 'menu-open' : ''}`}>
                                            <button
                                                className="action-menu-btn"
                                                onClick={(e) => toggleMenu(po.id, e)}
                                                title="Actions"
                                            >
                                                <MdMoreVert size={20} />
                                            </button>

                                            <ActionMenuPortal
                                                isOpen={openMenuId === po.id}
                                                anchorRect={anchorRect}
                                                onClose={() => setOpenMenuId(null)}
                                            >
                                                <div className="action-dropdown shadow-lg">
                                                    {/* <button className="action-item" onClick={() => handleEditClick(po.id)}>
                                                        <MdEdit size={16} /> Edit PO
                                                    </button> */}
                                                    <button className="action-item" onClick={() => navigate(`/reports/po/details/${po.id}`)}>
                                                        <MdVisibility size={16} /> View PO
                                                    </button>
                                                    <button className="action-item" onClick={() => navigate(`/po/entry/${po.id}`)}>
                                                        <MdInput size={16} /> PO Entry
                                                    </button>
                                                    <button className="action-item" onClick={() => handleGeneratePDF(po.id)}>
                                                        <MdDownload size={16} /> Generate PDF
                                                    </button>
                                                    <button className="action-item" onClick={() => openWhatsappModal(po)}>
                                                        <FaWhatsapp size={16} /> Share on WhatsApp
                                                    </button>
                                                    {hasPermission('PO_MANAGEMENT', 'update') && (
                                                        <button className="action-item" onClick={() => handlePoActionClick(po, 'completed', 'Force Complete PO')}>
                                                            <MdAdd size={16} /> Force Complete
                                                        </button>
                                                    )}
                                                    {hasPermission('PO_MANAGEMENT', 'delete') && (
                                                        <button className="action-item" onClick={() => handlePoActionClick(po, 'cancelled', 'Cancel PO')} style={{ color: '#d33' }}>
                                                            <MdClose size={16} /> Cancel PO
                                                        </button>
                                                    )}
                                                </div>
                                            </ActionMenuPortal>
                                        </td>
                                    </tr>
                                ))
                            })()}
                        </tbody>
                    </table>
                </div>

                {/* Mobile Card View */}
                <div className="mobile-card-view">
                    {loading ? (
                        <Loader text="Loading orders..." />
                    ) : orders.length === 0 ? (
                        <NoData text={activeTab === 'pending' ? "No pending PO found" : activeTab === 'completed' ? "No complete data found" : activeTab === 'cancelled' ? "No cancelled data found" : "No orders found."} />
                    ) : (() => {
                        const filteredOrders = orders.filter(po => {
                            const status = (po.status || 'pending').toLowerCase();
                            if (activeTab === 'pending') {
                                return status === 'pending' || status === 'partial' || status === 'partial_received' || status === 'partially received';
                            }
                            if (activeTab === 'completed') {
                                return status === 'completed' || status === 'complete' || status === 'force_completed' || status === 'forced_completed' || status === 'force complete';
                            }
                            if (activeTab === 'cancelled') {
                                return status === 'cancelled' || status === 'cancel';
                            }
                            return true;
                        });

                        if (filteredOrders.length === 0) {
                            return <NoData text={activeTab === 'pending' ? "No pending PO found" : activeTab === 'completed' ? "No complete data found" : activeTab === 'cancelled' ? "No cancelled data found" : "No orders found."} />;
                        }

                        return filteredOrders.map((po) => (
                            <div key={po.id} className="dashboard-card">
                                <div className="card-header">
                                    <div className="card-title-po">
                                        <strong className="po-number" style={{ fontFamily: 'monospace' }}>#{po.poNumber || "N/A"}</strong>
                                        <span className={`status-badge status-${(po.status || 'pending').toLowerCase()}`}>
                                            {po.status === 'force_completed' ? 'Force Completed' : (po.status || 'Pending')}
                                        </span>
                                    </div>
                                    <button
                                        className="action-menu-btn"
                                        onClick={(e) => toggleMenu(po.id, e)}
                                        title="Actions"
                                    >
                                        <MdMoreVert size={20} />
                                    </button>
                                    <ActionMenuPortal
                                        isOpen={openMenuId === po.id}
                                        anchorRect={anchorRect}
                                        onClose={() => setOpenMenuId(null)}
                                    >
                                        <div className="action-dropdown shadow-lg">
                                            {/* <button className="action-item" onClick={() => handleEditClick(po.id)}>
                                                <MdEdit size={16} /> Edit PO
                                            </button> */}
                                            <button className="action-item" onClick={() => navigate(`/reports/po/details/${po.id}`)}>
                                                <MdVisibility size={16} /> View PO
                                            </button>
                                            <button className="action-item" onClick={() => navigate(`/po/entry/${po.id}`)}>
                                                <MdInput size={16} /> PO Entry
                                            </button>
                                            <button className="action-item" onClick={() => handleGeneratePDF(po.id)}>
                                                <MdDownload size={16} /> Generate PDF
                                            </button>
                                            <button className="action-item" onClick={() => openWhatsappModal(po)}>
                                                <FaWhatsapp size={16} /> Share on WhatsApp
                                            </button>
                                            {hasPermission('PO_MANAGEMENT', 'update') && (
                                                <button className="action-item" onClick={() => handlePoActionClick(po, 'completed', 'Force Complete PO')}>
                                                    <MdAdd size={16} /> Force Complete
                                                </button>
                                            )}
                                            {hasPermission('PO_MANAGEMENT', 'delete') && (
                                                <button className="action-item" onClick={() => handlePoActionClick(po, 'cancelled', 'Cancel PO')} style={{ color: '#d33' }}>
                                                    <MdClose size={16} /> Cancel PO
                                                </button>
                                            )}
                                        </div>
                                    </ActionMenuPortal>
                                </div>
                                <div className="card-body">
                                    <div className="card-row">
                                        <span className="card-label">Vendor:</span>
                                        <span className="card-value">
                                            {(po.PurchaseOrderVendors || [])
                                                .filter(v => v?.Vendor)
                                                .length > 0 ? (
                                                (po.PurchaseOrderVendors || [])
                                                    .filter(v => v?.Vendor)
                                                    .map((v, index) => (
                                                        <div key={index}>
                                                            <strong>{v.Vendor.vendorName || "N/A"}</strong>
                                                        </div>
                                                    ))
                                            ) : (
                                                <strong>N/A</strong>
                                            )}
                                        </span>
                                    </div>
                                    <div className="card-row">
                                        <span className="card-label">Project:</span>
                                        <span className="card-value">{po.ProjectMaster?.projectName || po.projectName}</span>
                                    </div>
                                    <div className="card-row">
                                        <span className="card-label">Date:</span>
                                        <span className="card-value">{formatDate(po.date)}</span>
                                    </div>
                                    <div className="card-row">
                                        <span className="card-label">Total Amount:</span>
                                        <span className="card-value">₹{po.grandTotal ? parseFloat(po.grandTotal).toLocaleString() : '0'}</span>
                                    </div>
                                    <div className="card-row" style={{ marginTop: '4px', paddingTop: '8px', borderTop: '1px solid #f3f4f6' }}>
                                        <span className="card-label">Notification:</span>
                                        <span className="card-value">
                                            <label className="switch">
                                                <input
                                                    type="checkbox"
                                                    checked={po.notification || false}
                                                    onChange={() => handleNotificationToggle(po.id, po.notification || false)}
                                                />
                                                <span className="slider"></span>
                                            </label>
                                        </span>
                                    </div>
                                </div>
                            </div>
                        ))
                    })()}
                </div>
                <div className={isMobile ? "pagination-card" : ""}>
                    <Pagination
                        currentPage={currentPage}
                        totalPages={totalPages}
                        onPageChange={handlePageChange}
                        totalRecords={totalRecords}
                        currentCount={orders.length}
                        entityName="Purchase Orders"
                    />
                </div>
            </div>

            {/* WhatsApp Share Modal */}
            {
                showWhatsappModal && (
                    <div className="modal-overlay" onClick={() => setShowWhatsappModal(false)}>
                        <div className="modal-content share-modal-content" onClick={e => e.stopPropagation()}>
                            <div className="modal-header">
                                <h3>Share on Whatsapp</h3>
                                <button className="close-btn" onClick={() => setShowWhatsappModal(false)}>
                                    <MdClose size={20} />
                                </button>
                            </div>
                            <div className="modal-body">
                                <p className="share-instruction">
                                    Select vendors to send the Purchase Order PDF via Whatsapp.
                                </p>


                                {loadingVendors ? (
                                    <div style={{ display: 'flex', justifyContent: 'center', padding: '2rem' }}>
                                        <Loader text="Loading vendor details..." />
                                    </div>
                                ) : selectedVendorsForWhatsapp.length === 0 ? (
                                    <p className="no-data">No vendors found for this order.</p>
                                ) : (
                                    <div className="vendor-links-list">
                                        {selectedVendorsForWhatsapp.map((vendor) => (
                                            <div key={vendor.vendorId} className="vendor-link-item">
                                                <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                                                    <input
                                                        type="checkbox"
                                                        id={`vendor-${vendor.vendorId}`}
                                                        checked={vendor.isChecked}
                                                        onChange={() => handleWhatsappCheckboxChange(vendor.vendorId)}
                                                        style={{ width: '18px', height: '18px', cursor: 'pointer' }}
                                                        disabled={!vendor.mobileNumber}
                                                    />
                                                    <div className="vendor-info">
                                                        <span className="vendor-name">{vendor.vendorName}</span>
                                                        <span className="vendor-email">
                                                            {vendor.mobileNumber ? `+${vendor.countryCode} ${vendor.mobileNumber}` : 'No Number'}
                                                        </span>
                                                    </div>
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                )}
                                <div className="form-actions">
                                    <button
                                        className="save-btn"
                                        onClick={handleSendWhatsapp}
                                        disabled={loadingVendors || sendingWhatsapp || selectedVendorsForWhatsapp.filter(v => v.isChecked).length === 0}
                                    >
                                        {sendingWhatsapp ? 'Sending...' : (
                                            <>
                                                <FaWhatsapp size={18} /> Send WhatsApp
                                            </>
                                        )}
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>
                )
            }

            {/* PO Action Modal */}
            {
                showActionModal && (
                    <div className="modal-overlay" onClick={() => setShowActionModal(false)}>
                        <div className="modal-content" onClick={e => e.stopPropagation()} style={{ maxWidth: '500px' }}>
                            <div className="modal-header">
                                <h3>{actionModalData.actionTitle}</h3>
                                <button className="close-btn" onClick={() => setShowActionModal(false)}>
                                    <MdClose size={20} />
                                </button>
                            </div>
                            <div className="modal-body">
                                {actionModalData.newStatus === 'completed' ? (
                                    <>
                                        <p className="share-instruction" style={{ marginBottom: '1rem' }}>
                                            Please provide a remark for changing the status to <strong>{actionModalData.newStatus}</strong>:
                                        </p>
                                        <div className="form-group" style={{ marginBottom: '1.5rem' }}>
                                            <textarea
                                                className="form-input"
                                                rows="4"
                                                placeholder="Enter your remark here..."
                                                value={actionRemark}
                                                onChange={(e) => setActionRemark(e.target.value)}
                                                style={{ width: '100%', resize: 'vertical' }}
                                            ></textarea>
                                        </div>
                                    </>
                                ) : (
                                    <p className="share-instruction" style={{ marginBottom: '1.5rem', fontSize: '1.1rem' }}>
                                        Are you sure you want to cancel this Purchase Order?
                                    </p>
                                )}
                                <div className="form-actions" style={{ justifyContent: 'flex-end', gap: '10px' }}>
                                    <button
                                        className="cancel-btn"
                                        onClick={() => setShowActionModal(false)}
                                        disabled={submittingAction}
                                    >
                                        Close
                                    </button>
                                    <button
                                        className="save-btn"
                                        onClick={submitPoAction}
                                        disabled={submittingAction || (actionModalData.newStatus === 'completed' && !actionRemark.trim())}
                                        style={{
                                            background: actionModalData.newStatus === 'cancelled' ? '#dc2626' : '#10b981',
                                            borderColor: actionModalData.newStatus === 'cancelled' ? '#dc2626' : '#10b981'
                                        }}
                                    >
                                        {submittingAction ? 'Processing...' : 'Submit'}
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>
                )
            }
        </div>
    );
};

export default POManagement;
